/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import SelectBox from "components/SelectBox";
import {Option} from "types/entities/specific/Option";
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";

interface CustomHeaderProps {
    date: Date;
    changeYear: (year: number) => void;
    changeMonth: (month: number) => void;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
}

const DatePickerCustomHeader: React.FC<CustomHeaderProps> = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}) => {
    const currentDate = new Date();
    const currentDateYear = currentDate.getFullYear();
    const currentDateMonth = currentDate.getMonth();

    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth() + 1;

    const years = Array.from({length: 2}, (_, i) => currentDateYear + i);
    const months = Array.from({length: 12}, (_, i) => i);
    const [selectedYear, setSelectedYear] = useState<number>(currentYear + 1);
    const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);

    const handleYearChange = (option: Option) => {
        setSelectedYear(option.value);
        changeYear(option.value);
    };

    const handleMonthChange = (option: Option) => {
        setSelectedMonth(option.value);
        changeMonth(option.value);
    };

    const yearsOptions = years.map(year => {
        return {
            label: year.toString(),
            value: year.valueOf(),
        };
    });

    const filteredMonths = months.filter((month: number) => {
        if (currentYear <= currentDateYear) {
            return month >= currentDateMonth;
        }
        return true;
    });

    const monthsOptions = filteredMonths.map(month => {
        return {
            label: new Date(currentYear, month).toLocaleString("default", {month: "long"}),
            value: month.valueOf(),
        };
    });

    useEffect(() => {
        setSelectedMonth(currentMonth - 1);
    }, [currentMonth]);

    useEffect(() => {
        setSelectedYear(currentYear);
    }, [currentYear]);

    return (
        <div
            className={
                "react-datepicker__header--info w-full max-w-full flex flex-row justify-between items-center space-x-2 py-2 px-2 bg-gray-100 rounded-tl-md rounded-tr-md shadow-sm z-10 relative"
            }
        >
            <button
                className={
                    "flex-none px-2 py-2 bg-arbo-orange rounded-md border border-arbo-orange hover:bg-white text-white hover:text-arbo-orange duration-150 transition-all"
                }
                disabled={prevMonthButtonDisabled}
                onClick={decreaseMonth}
            >
                <ChevronLeftIcon className={"w-5 h-5 flex-none"} />
            </button>
            <div className={"flex-1 flex flex-row items-center justify-between space-x-2 w-auto max-w-full"}>
                <SelectBox label={currentDateYear.toString()} options={yearsOptions} selected={selectedYear} setSelected={handleYearChange} />
                <SelectBox label={currentDateMonth.toString()} options={monthsOptions} selected={selectedMonth} setSelected={handleMonthChange} />
            </div>
            <button
                className={clsx(
                    nextMonthButtonDisabled ? "bg-gray-300 border-gray-300" : "bg-arbo-orange border-arbo-orange  hover:bg-white ",
                    "flex-none px-2 py-2 bg-arbo-orange rounded-md border text-white hover:text-arbo-orange duration-150 transition-all"
                )}
                disabled={nextMonthButtonDisabled}
                onClick={increaseMonth}
            >
                <ChevronRightIcon className={"w-5 h-5 flex-none"} />
            </button>
        </div>
    );
};

export default DatePickerCustomHeader;
